import { useSyncExternalStore } from 'react';
import { api, ApiError, type ActionKind, type ActionResult } from './api.js';
import { onWireState } from './stream.js';

/* Every phone action is a request with an id (Ops V7 design §2). The app
 * chooses the id, shows the request as pending, then done or failed with
 * the reason and a retry; a request the network swallowed is kept and
 * sent again when the wire comes back, and the daemon answers the same
 * way to the same id, so nothing is ever applied twice or lost in
 * silence. Pending requests survive a reload in localStorage. */

export interface PendingAction {
  id: string;
  jobId: string;
  kind: ActionKind;
  payload: Record<string, unknown>;
  label: string;
  state: 'pending' | 'done' | 'failed';
  error: string | null;
  createdAt: string;
  attempts: number;
}

const KEY = 'ops.actions.v7';
let list: PendingAction[] = [];
try {
  const raw = localStorage.getItem(KEY);
  list = raw ? (JSON.parse(raw) as PendingAction[]) : [];
} catch {
  list = [];
}
const listeners = new Set<() => void>();
const emit = () => {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.filter((a) => a.state === 'pending' || Date.now() - Date.parse(a.createdAt) < 3600_000).slice(-50)));
  } catch {
    // storage may be unavailable
  }
  listeners.forEach((l) => l());
};
const update = (id: string, patch: Partial<PendingAction>) => {
  list = list.map((a) => (a.id === id ? { ...a, ...patch } : a));
  emit();
};

async function send(a: PendingAction): Promise<ActionResult | null> {
  update(a.id, { attempts: a.attempts + 1 });
  try {
    const r = await api.post<ActionResult>(`/api/jobs/${a.jobId}/actions`, { id: a.id, kind: a.kind, ...a.payload });
    update(a.id, { state: r.ok ? 'done' : 'failed', error: r.ok ? null : r.action?.error ?? r.error ?? 'The daemon refused it.' });
    return r;
  } catch (err) {
    if (err instanceof ApiError && err.status > 0) {
      // The daemon answered: a refusal is final, not a retry.
      let msg = err.message;
      update(a.id, { state: 'failed', error: msg });
      return null;
    }
    // No answer at all: stays pending, retried when the wire returns.
    update(a.id, { error: err instanceof Error ? err.message : 'No answer from the box.' });
    return null;
  }
}

export async function runAction(jobId: string, kind: ActionKind, payload: Record<string, unknown> = {}, label: string = kind): Promise<ActionResult | null> {
  const a: PendingAction = {
    id: crypto.randomUUID(),
    jobId,
    kind,
    payload,
    label,
    state: 'pending',
    error: null,
    createdAt: new Date().toISOString(),
    attempts: 0,
  };
  list = [...list, a];
  emit();
  return send(a);
}

export function retryAction(id: string): void {
  const a = list.find((x) => x.id === id);
  if (!a) return;
  update(id, { state: 'pending', error: null });
  void send({ ...a, state: 'pending' });
}

export function dismissAction(id: string): void {
  list = list.filter((a) => a.id !== id);
  emit();
}

/* Pending requests are sent again when the wire comes back and every 15 s
   while any is waiting. */
let retrying = false;
async function retryPending(): Promise<void> {
  if (retrying) return;
  retrying = true;
  try {
    for (const a of list.filter((x) => x.state === 'pending' && x.attempts > 0)) await send(a);
  } finally {
    retrying = false;
  }
}
if (typeof window !== 'undefined') {
  onWireState((s) => {
    if (s === 'live') void retryPending();
  });
  window.setInterval(() => {
    if (list.some((a) => a.state === 'pending' && a.attempts > 0)) void retryPending();
  }, 15_000);
}

export function useActions(jobId: string): PendingAction[] {
  return useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    () => list,
    () => list,
  ).filter((a) => a.jobId === jobId);
}
